import { CommonModule, type KeyValue } from '@angular/common';
import {
  Component,
  HostBinding,
  Input,
  ViewEncapsulation,
  inject,
  input,
} from '@angular/core';
import {
  BaseChartWithMultipleTimeSeries,
  type BaseChartWithMultipleTimeSeriesParams,
  TimeSeriesFacade,
} from '@backoffice-frontend/time-series-identifier/domain';
import type { ChartTimeSeries, TimeSeriesInput, TimeSeriesPoint } from '@clean-code/models';
import { indicate } from '@clean-code/shared/common';
import {
  DevExtremeLineChartComponent,
  type LineChartModel,
  toLineChartViewModel,
} from '@clean-code/shared/components/ui-devextreme-charts';
import { ProgressBarComponent } from '@clean-code/shared/components/ui-progress-bar';
import { TranslocoModule } from '@jsverse/transloco';
import { concat, of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';

type ComparisonChartViewModel =
  | { type: 'loading' }
  | { type: 'error'; error: KeyValue<string, string> }
  | {
      type: 'chart';
      chartData: LineChartModel[];
      table: Array<{
        name: string;
        value: number;
        unit: string;
      }>;
    };

function toComparisonChartViewModel(
  values: ChartTimeSeries[],
  showTable: boolean,
): ComparisonChartViewModel {
  const chartView = toLineChartViewModel(values);
  if (chartView.type !== 'chart') {
    return chartView;
  }

  const table = new Array<{
    name: string;
    value: number;
    unit: string;
  }>();

  values.forEach((value: ChartTimeSeries) => {
    if (showTable && !value.frontOfficeName?.includes('_F_')) {
      const latest = value.values.reduce<TimeSeriesPoint | undefined>(
        (current, point) => {
          if (
            !current?.tradeDate ||
            point.tradeDate.getTime() > current.tradeDate.getTime()
          ) {
            return point;
          }
          return current;
        },
        undefined,
      );

      table.push({
        name: value.frontOfficeName,
        value: latest?.value ?? 0,
        unit: value.unit,
      });
    }
  });

  return {
    type: 'chart',
    chartData: chartView.chartData,
    table,
  };
}

@Component({
  selector: 'fof-comparison-chart',
  styleUrls: ['./comparison-chart.component.scss'],
  templateUrl: './comparison-chart.component.html',
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    TranslocoModule,
    DevExtremeLineChartComponent,
    ProgressBarComponent,
  ],
})
export class ComparisonChartComponent extends BaseChartWithMultipleTimeSeries {
  private timeSeriesFacade = inject(TimeSeriesFacade);

  @HostBinding('class') class = 'fof-comparison-chart';

  public override paramChange$ = this.params$.pipe(
    filter((params: BaseChartWithMultipleTimeSeriesParams) => !!params),
  );

  @Input({ required: true })
  public set params(value: BaseChartWithMultipleTimeSeriesParams) {
    this.params$.next(value);
  }

  legend = input(true);

  public chartView$ = this.paramChange$.pipe(
    switchMap((params: BaseChartWithMultipleTimeSeriesParams) =>
      concat(
        of({ type: 'loading' } as const),
        this.timeSeriesFacade
          .chartComparisonTimeSeries$(
            params.identifiers.map((x: TimeSeriesInput) => x.identifier),
            params.timeFrame,
            params.showInPercentage,
          )
          .pipe(
            indicate(this.isLoading$),
            map((values: ChartTimeSeries[]) =>
              toComparisonChartViewModel(values, !!params.showTable),
            ),
          ),
      ),
    ),
  );

  hideFactorLegendItems(items: { text: string }[]): { text: string }[] {
    return items.filter((item) => !item.text.includes('_F_'));
  }
}
